import React, { useState } from "react";
import { Form, Select, Table, Input, InputNumber, Button, Space, Alert } from "antd";
import { PlusOutlined, DeleteOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { BidItem } from "../interface";
import { ItemSelectionDialog } from "./ItemSelectionDialog";

interface BidFormProps {
  value?: {
    items: BidItem[];
    totalAmount: number;
    currency?: string;
    notes?: string;
    title?: string;
    description?: string;
  };
  onChange?: (value: {
    items: BidItem[];
    totalAmount: number;
    currency?: string;
    notes?: string;
    title?: string;
    description?: string;
  }) => void;
  selectedPrIds?: string[];
  onPrChange?: (prIds: string[]) => void;
  disabled?: boolean;
}

export const BidForm: React.FC<BidFormProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [form] = Form.useForm();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleItemsChange = (items: BidItem[]) => {
    const totalAmount = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const newValue = {
      ...value,
      items,
      totalAmount,
    };
    onChange?.(newValue);
  };

  const addItem = () => {
    const items = [...(value?.items || [])];
    items.push({
      id: `temp-${Date.now()}`,
      name: "",
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    });
    handleItemsChange(items);
  };

  const removeItem = (index: number) => {
    const items = [...(value?.items || [])];
    items.splice(index, 1);
    handleItemsChange(items);
  };

  const updateItem = (index: number, field: keyof BidItem, newValue: any) => {
    const items = [...(value?.items || [])];
    items[index] = { ...items[index], [field]: newValue };

    if (field === 'quantity' || field === 'unitPrice') {
      items[index].totalPrice = (items[index].quantity || 0) * (items[index].unitPrice || 0);
    }

    handleItemsChange(items);
  };

  const bidItemColumns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, _: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateItem(index, "name", e.target.value)}
          placeholder="Item name"
        />
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text: string, _: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateItem(index, "description", e.target.value)}
          placeholder="Description"
        />
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: 120,
      render: (value: number, _: any, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateItem(index, "quantity", val || 0)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      width: 100,
      render: (text: string, _: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => updateItem(index, "unit", e.target.value)}
          placeholder="Unit"
        />
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      width: 120,
      render: (value: number, _: any, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateItem(index, "unitPrice", val || 0)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      width: 120,
      render: (value: number) => `ETB ${Number(value || 0).toLocaleString()}`,
    },
    {
      title: "Actions",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeItem(index)} />
      ),
    },
  ];

  return (
    <Form form={form} layout="vertical">
      <Form.Item
        label="Title"
        rules={[{ required: true, message: 'Please enter sourcing title' }]}
      >
        <Input
          placeholder="Enter sourcing title"
          value={value?.title}
          onChange={(e) => onChange?.({
            ...value,
            title: e.target.value
          } as any)}
          disabled={disabled}
        />
      </Form.Item>

      <Form.Item
        label="Description"
        rules={[{ required: true, message: 'Please enter sourcing description' }]}
      >
        <Input.TextArea
          rows={3}
          placeholder="Describe the sourcing opportunity..."
          value={value?.description}
          onChange={(e) => onChange?.({
            ...value,
            description: e.target.value
          } as any)}
          disabled={disabled}
        />
      </Form.Item>

      <Form.Item label="Bid Items">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Button type="dashed" onClick={addItem} icon={<PlusOutlined />}>
              Add Item Manually
            </Button>
            <Button type="primary" icon={<ShoppingCartOutlined />} onClick={() => setIsDialogOpen(true)}>
              Add Items from PR
            </Button>
          </Space>
          {(value?.items || []).length === 0 && (
            <Alert
              message="Click 'Add Items from PR' to select items from approved Purchase Requisitions."
              type="info"
              showIcon
            />
          )}
          <Table
            dataSource={value?.items || []}
            columns={bidItemColumns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Space>
      </Form.Item>

      <Form.Item label="Total Amount">
        <Input
          value={`ETB ${(value?.totalAmount || 0).toLocaleString()}`}
          readOnly
          style={{ fontWeight: 'bold' }}
        />
      </Form.Item>

      <Form.Item label="Currency">
        <Select
          placeholder="Select currency"
          value={value?.currency}
          onChange={(val) => onChange?.({
            ...value,
            currency: val
          } as any)}
        >
          <Select.Option value="ETB">ETB - Ethiopian Birr</Select.Option>
          <Select.Option value="USD">USD - US Dollar</Select.Option>
          <Select.Option value="EUR">EUR - Euro</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="Notes">
        <Input.TextArea
          rows={3}
          placeholder="Additional notes for the bid"
          value={value?.notes}
          onChange={(e) => onChange?.({
            ...value,
            notes: e.target.value
          } as any)}
        />
      </Form.Item>
      <ItemSelectionDialog
        open={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onConfirm={(newItems) => {
          const mergedItems = [...(value?.items || []), ...newItems];
          handleItemsChange(mergedItems);
          setIsDialogOpen(false);
        }}
      />
    </Form>
  );
};
